import { useEffect, useState, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { MediaCard } from "../components/MediaCard";
import { UploadDialog } from "../components/UploadDialog";
import { AISearchDialog } from "../components/AISearch";
import { GhostTiles } from "../components/GhostTiles";
import { ScratchPicker } from "../components/ScratchPicker";
import { VlogTemplates } from "../components/VlogTemplates";
import { PremiumBadge, PremiumLock } from "../components/PremiumLock";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, Sparkles, FolderPlus, Sticker, Clapperboard } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const KINDS = [
  { id: "all", label: "All" },
  { id: "video", label: "Video" },
  { id: "image", label: "Image" },
  { id: "audio", label: "Audio" },
  { id: "document", label: "Documents" },
];

export default function Library() {
  const { user, isPremium } = useAuth();
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchSeed, setSearchSeed] = useState("");
  const [scratchOpen, setScratchOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/media", { params: kind === "all" ? {} : { kind } });
      setItems(res.data || []);
    } catch (e) {
      toast.error("Failed to load media");
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleGhostPick = (prompt) => {
    setSearchSeed(prompt);
    setSearchOpen(true);
  };

  return (
    <div className="min-h-screen bg-white text-black" data-testid="library-page">
      <Navbar onSearchOpen={() => setSearchOpen(true)} onUploadOpen={() => setUploadOpen(true)} />

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E60000]">Library</div>
              <PremiumBadge user={user} />
            </div>
            <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter leading-[0.9] mt-2">
              The vault.
            </h1>
            <p className="mt-2 text-black/55 max-w-md">Everything your squad has ever shipped — searchable in plain english.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setTplOpen(true)} data-testid="open-templates">
              <Clapperboard className="w-4 h-4 mr-2 text-[#E60000]" /> Templates
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={() => setScratchOpen(true)} data-testid="open-scratch">
              <Sticker className="w-4 h-4 mr-2 text-[#E60000]" /> Scratch
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={() => setSearchOpen(true)} data-testid="open-ai-search">
              <Sparkles className="w-4 h-4 mr-2 text-[#E60000]" /> Ask AI
            </Button>
            <Button className="rounded-sm bg-[#E60000] hover:bg-[#CC0000] text-white" onClick={() => setUploadOpen(true)} data-testid="open-upload">
              <Plus className="w-4 h-4 mr-1" /> Upload
            </Button>
          </div>
        </div>

        <GhostTiles onPick={handleGhostPick} />

        {!isPremium && <PremiumLock feature="Premium" className="mb-8" />}

        <Tabs value={kind} onValueChange={setKind} className="mb-8">
          <TabsList className="rounded-sm h-10 bg-transparent border border-black/10 p-0">
            {KINDS.map((k) => (
              <TabsTrigger
                key={k.id}
                value={k.id}
                data-testid={`tab-${k.id}`}
                className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white px-5 h-full font-medium tracking-tight"
              >
                {k.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-video bg-black/5 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-black/15 p-16 text-center" data-testid="empty-state">
            <FolderPlus className="w-10 h-10 mx-auto text-black/30" strokeWidth={1.5} />
            <h3 className="mt-4 font-heading font-black text-2xl tracking-tight">Your vault is empty.</h3>
            <p className="mt-2 text-black/50">Drag your first masterpiece here. Faster sync handles the rest.</p>
            <Button className="mt-6 rounded-sm bg-[#E60000] hover:bg-[#CC0000] text-white" onClick={() => setUploadOpen(true)} data-testid="empty-upload">
              <Plus className="w-4 h-4 mr-1" /> Upload media
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="media-grid">
            {items.map((m, i) => <MediaCard key={m.id} media={m} index={i} />)}
          </div>
        )}
      </div>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={fetchAll} />
      <AISearchDialog open={searchOpen} onOpenChange={(v) => { setSearchOpen(v); if (!v) setSearchSeed(""); }} initialQuery={searchSeed} />
      <ScratchPicker open={scratchOpen} onOpenChange={setScratchOpen} onPickAsset={(a) => toast.success(`${a.name} ready · drop into your edit`)} />
      <VlogTemplates open={tplOpen} onOpenChange={setTplOpen} onPickTemplate={() => {}} />
    </div>
  );
}
